import { makeAutoObservable, runInAction } from "mobx";
import * as signalR from "@microsoft/signalr";
import { AppStore } from "store";
import { ContentHubMethods } from "models/dto/ZoomiLxp/Utilities/Constants/ContentHubMethods";

export class SignalRStore {
	connection?: signalR.HubConnection;

	private rootStore;
	// Serialises connect/disconnect. React does not await the promise an effect cleanup
	// returns, so an access token change ran both at once: disconnect() suspended on the
	// in-flight start(), connect() saw that same in-flight promise and returned without
	// doing anything, and disconnect() then tore the connection down for good — leaving
	// the store permanently disconnected until the next token change. Chaining every call
	// onto one queue makes the last caller win in call order, so a cleanup followed by a
	// connect ends up connected.
	private queue: Promise<unknown> = Promise.resolve();

	constructor(rootStore: AppStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
	}

	get isConnected() {
		return this.connection?.state === signalR.HubConnectionState.Connected;
	}

	connect(): Promise<void> {
		return this.enqueue(() => this.openConnection());
	}

	disconnect(): Promise<void> {
		return this.enqueue(() => this.closeConnection());
	}

	private enqueue(operation: () => Promise<void>): Promise<void> {
		const result = this.queue.then(operation, operation);
		// The queue itself must never carry a rejection forward: one failed start() would
		// otherwise make every later connect and disconnect reject as well.
		this.queue = result.catch(() => undefined);
		return result;
	}

	private async openConnection() {
		if (!this.rootStore.accountStore.accessToken) {
			return;
		}

		if (this.connection?.state === signalR.HubConnectionState.Connected) {
			return;
		}

		const connection = this.connection ?? this.createConnection();

		runInAction(() => {
			this.connection = connection;
		});

		// Automatic reconnect drives itself; starting on top of it would throw.
		if (
			connection.state === signalR.HubConnectionState.Connecting ||
			connection.state === signalR.HubConnectionState.Reconnecting
		) {
			return;
		}

		await connection.start();
	}

	private async closeConnection() {
		if (!this.connection) {
			return;
		}

		const currentConnection = this.connection;

		runInAction(() => {
			this.connection = undefined;
		});

		if (currentConnection.state !== signalR.HubConnectionState.Disconnected) {
			await currentConnection.stop();
		}
	}

	private createConnection() {
		const hubUrl = "/api/v1/chathub";

		const accessTokenFactory = async () => {
			return this.rootStore.accountStore.accessToken ?? "";
		};

		const connection = new signalR.HubConnectionBuilder()
			.withUrl(hubUrl, {
				accessTokenFactory,
			})
			.withAutomaticReconnect()
			.build();

		connection.on(ContentHubMethods.ItemCompleted, (courseSequenceId: number) => {
			this.rootStore.coursesStore.handleSignalRContentCompleted(courseSequenceId);
		});

		connection.onreconnected(async () => {
			const coursesStore = this.rootStore.coursesStore;
			const courseId = coursesStore.currentCourse?.id;
			// The last opened course stays in the store after the user navigates away, so
			// without the view check this refetches a course nobody is looking at.
			if (!courseId || !coursesStore.isCourseViewActive) {
				return;
			}

			// Pass the item the learner is on: getCourseById falls back to
			// lastWatchedCourseItem, or index 0, when no item id is given, which would
			// move them off their current content. showLoading stays off so a dropped
			// connection does not flash the global spinner over whatever page they are on.
			const currentItemId = coursesStore.activeCourseItems[coursesStore.curPositionContent]?.courseItemId;

			try {
				await coursesStore.getCourseById(courseId, currentItemId, false);
			} catch {
				// Best-effort resync of completions missed while disconnected. On failure the
				// currently rendered state is kept; the next navigation reloads the course.
			}
		});

		return connection;
	}
}
